var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useParams } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import useSWR from "swr";
import { apiFetch, LoadingIcon, SingleStat, StyledButton, StyledButtonStyle, } from "ee-react-components";
import { Permission, isIncidentActive, } from "ee-nestjs-interfaces";
import { process } from "../Process";
import { BackButton } from "../Components/BackButton";
import { usePermissions } from "../PermissionsContext";
import { IncidentEditDialog } from "../Components/Incidents/IncidentEditDialog";
import { IncidentAuditLogDialog } from "../Components/Incidents/IncidentAuditLogDialog";
import { impactLabel, issueLabel, } from "../Components/Incidents/IncidentLabels";
import { TicketStat } from "../Components/Incidents/TicketLink";
/**
 * How long an incident ran, worded for the stat on its page.
 *
 * A recorded end fixes the span, even one still in the future: a planned window
 * is a length already agreed, and counting up to it instead would disagree with
 * the End stat sitting beside it. With no end recorded the span is still open,
 * so it reports what has elapsed and says as much rather than presenting a
 * growing number as a total.
 */
function incidentDuration(incident) {
    var now = Date.now();
    var beganAt = new Date(incident.beginning).getTime();
    if (incident.end != null) {
        return formatDuration(new Date(incident.end).getTime() - beganAt);
    }
    // An incident may be filed ahead of the window it covers — planned
    // maintenance is declared before it starts. Nothing has elapsed yet, and
    // "< 1m so far" would claim otherwise.
    if (beganAt > now)
        return "Not started";
    return "".concat(formatDuration(now - beganAt), " so far");
}
/**
 * A span of milliseconds at the granularity incidents are read in: days, hours
 * and minutes, dropping whichever of those are zero.
 *
 * Seconds are noise against incidents that run for days, so anything shorter
 * than a minute collapses to "< 1m" instead of rounding down to a bare "0m".
 * Negative spans — an end recorded before the beginning — land there too.
 */
function formatDuration(ms) {
    var totalMinutes = Math.floor(ms / 60000);
    if (totalMinutes < 1)
        return "< 1m";
    var days = Math.floor(totalMinutes / 1440);
    var hours = Math.floor((totalMinutes % 1440) / 60);
    var minutes = totalMinutes % 60;
    return [
        days > 0 ? "".concat(days, "d") : null,
        hours > 0 ? "".concat(hours, "h") : null,
        minutes > 0 ? "".concat(minutes, "m") : null,
    ]
        .filter(Boolean)
        .join(" ");
}
/**
 * One incident, at its own address.
 *
 */
export var Incident = function (_a) {
    var _b, _c, _d, _e, _f;
    var isNoc = _a.isNoc;
    var uid = useParams().uid;
    var navigate = useNavigate();
    var _g = useAuth0(), isAuthenticated = _g.isAuthenticated, isLoading = _g.isLoading, getAccessTokenSilently = _g.getAccessTokenSilently;
    var permissions = usePermissions();
    var _h = useState("none"), dialog = _h[0], setDialog = _h[1];
    var getToken = useCallback(function () {
        return getAccessTokenSilently({
            authorizationParams: { audience: process.env.AUTH0_AUDIENCE },
        });
    }, [getAccessTokenSilently]);
    var _j = useSWR(isLoading || !isAuthenticated || !uid
        ? null
        : "".concat(process.env.NOC_SVC_URL, "/incidents/").concat(uid), function (url) {
        return apiFetch(url, { method: "GET", callbacks: { getToken: getToken } });
    }), incident = _j.data, error = _j.error, mutate = _j.mutate;
    // Fetched for the charger names and the PowerNode list the edit form offers.
    // Keyed off the incident, so it only runs once we know which site to ask
    // about — and not at all for a global incident, which names none. The form
    // then offers empty charger/PowerNode pickers, which is the honest answer:
    // there is no one site whose equipment it could list.
    var site = useSWR(incident == null || incident.siteId == null
        ? null
        : "".concat(process.env.CORE_SVC_URL, "/site/detail/").concat(incident.siteId), function (url) {
        return apiFetch(url, { method: "GET", callbacks: { getToken: getToken } });
    }).data;
    // The suppressions this incident raised, so the edit form can show what is
    // already there instead of only offering to add another.
    var _k = useSWR(isLoading || !isAuthenticated || !uid
        ? null
        : "".concat(process.env.NOC_SVC_URL, "/incidents/").concat(uid, "/alert-suppressions"), function (url) {
        return apiFetch(url, {
            method: "GET",
            callbacks: { getToken: getToken },
        });
    }), alertSuppressions = _k.data, mutateSuppressions = _k.mutate;
    // A global incident belongs to no single site, so the site-scoped grant has
    // nothing to check against — editing one takes the org-wide permission.
    var canWrite = incident != null &&
        (incident.siteId == null
            ? permissions.hasPermission(Permission.WRITE_NOC)
            : permissions.hasPermissionForSite(Permission.WRITE_NOC, incident.siteId));
    var closeDialog = useCallback(function () { return setDialog("none"); }, []);
    var openEdit = useCallback(function () { return setDialog("edit"); }, []);
    var openAudit = useCallback(function () { return setDialog("audit"); }, []);
    var submitEdit = useCallback(
    // The form speaks `IncidentCreate` (its ticket is optional, which is how a
    // *new* incident asks for one to be opened). PUT has no such affordance, so
    // the ticket is required here and the body is narrowed to prove it.
    function (_a) { return __awaiter(void 0, void 0, void 0, function () {
        var changes;
        var opsTicket = _a.opsTicket, rest = __rest(_a, ["opsTicket"]);
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!opsTicket)
                        throw new Error("An OPS ticket is required.");
                    changes = __assign(__assign({}, rest), { opsTicket: opsTicket });
                    return [4 /*yield*/, apiFetch("".concat(process.env.NOC_SVC_URL, "/incidents/").concat(uid), {
                            method: "PUT",
                            body: changes,
                            callbacks: { getToken: getToken },
                        })];
                case 1:
                    _b.sent();
                    return [4 /*yield*/, mutate()];
                case 2:
                    _b.sent();
                    // An edit can add or rewrite a suppression, so the prefill has to be
                    // re-read or the form would reopen showing the previous values.
                    return [4 /*yield*/, mutateSuppressions()];
                case 3:
                    // An edit can add or rewrite a suppression, so the prefill has to be
                    // re-read or the form would reopen showing the previous values.
                    _b.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getToken, mutate, mutateSuppressions, uid]);
    // A page cannot outlive its subject: the detail endpoint 404s on a retired
    // incident, so step back to whatever list the operator arrived from.
    var submitDelete = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiFetch("".concat(process.env.NOC_SVC_URL, "/incidents/").concat(uid), {
                        method: "DELETE",
                        callbacks: { getToken: getToken },
                    })];
                case 1:
                    _a.sent();
                    navigate(-1);
                    return [2 /*return*/];
            }
        });
    }); }, [getToken, navigate, uid]);
    // An incident may name a charger since removed from the site, so fall back to
    // the bare id rather than dropping it from the list.
    var chargerNames = useMemo(function () {
        var _a;
        if (incident == null)
            return "";
        var byId = new Map(((_a = site === null || site === void 0 ? void 0 : site.chargers) !== null && _a !== void 0 ? _a : []).map(function (c) { return [c.id, c]; }));
        return incident.chargerIds
            .map(function (id) {
            var charger = byId.get(id);
            return charger
                ? "#".concat(charger.friendlyId, " (").concat(charger.ocppId, ")")
                : "#".concat(id);
        })
            .join(", ");
    }, [incident, site]);
    if (!isNoc)
        return null;
    if (error != null) {
        return (React.createElement("div", { className: "flex flex-col pl-[32px]" },
            React.createElement(BackButton, null),
            React.createElement("label", { className: "font-N5 text-red mt-[64px]" }, "Could not load this incident: ".concat(error.message))));
    }
    if (incident == null) {
        return (React.createElement("div", { className: "flex flex-col pl-[32px]" },
            React.createElement(BackButton, null),
            React.createElement("div", { className: "w-[32px] h-[32px] mt-[64px]" }, LoadingIcon)));
    }
    var active = isIncidentActive(incident.end);
    return (React.createElement("div", { className: "flex flex-col pl-[32px]" },
        React.createElement(BackButton, null),
        React.createElement("div", { className: "flex flex-row gap-x-4 items-center mt-[64px]" },
            React.createElement("label", { className: "font-N5 text-white" }, incident.title),
            React.createElement("label", { className: "font-N8 ".concat(active ? "text-red" : "text-black-400") }, active ? "ONGOING" : "CLOSED")),
        React.createElement("label", { className: "font-N8 text-black-400 mt-[6px]" }, "Last edited by ".concat((_b = incident.updatedBy) !== null && _b !== void 0 ? _b : "unknown", " on ").concat(new Date(incident.updatedAt).toLocaleString())),
        React.createElement("div", { className: "grid grid-cols-3 mt-[64px] gap-x-[32px] gap-y-[32px] w-fit" },
            React.createElement(SingleStat, { title: "Site", value: incident.siteId == null
                    ? "All sites"
                    : (_c = site === null || site === void 0 ? void 0 : site.name) !== null && _c !== void 0 ? _c : "#".concat(incident.siteId) }),
            React.createElement(SingleStat, { title: "Start", value: new Date(incident.beginning).toLocaleString() }),
            React.createElement(SingleStat, { title: "End", value: incident.end
                    ? new Date(incident.end).toLocaleString()
                    : "Ongoing - no end recorded", 
                // Red while the end has not passed: the incident is still live.
                valueClassName: active ? "text-red" : undefined }),
            React.createElement(SingleStat, { title: "Total Time", value: incidentDuration(incident) }),
            React.createElement(SingleStat, { title: "Impact", value: incident.impact.map(impactLabel).join(", ") }),
            React.createElement(SingleStat, { title: "Issue", value: incident.issue.map(issueLabel).join(", ") }),
            React.createElement(TicketStat, { title: "OPS Ticket", ticket: incident.opsTicket }),
            React.createElement(TicketStat, { title: "MAINT Jira Ticket", ticket: incident.maintenanceJiraTicket }),
            React.createElement(SingleStat, { title: "Maintenance ION Ticket", value: (_d = incident.maintenanceIonTicket) !== null && _d !== void 0 ? _d : "None" }),
            React.createElement(SingleStat, { title: "Chargers", value: chargerNames === "" ? "None" : chargerNames }),
            React.createElement(SingleStat, { title: "PowerNode", value: incident.powerNodeId == null ? "None" : "".concat(incident.powerNodeId) }),
            React.createElement(SingleStat, { title: "Alert Suppression Synced", value: incident.syncAlertSuppressionTimes ? "Yes" : "No" })),
        React.createElement("hr", { className: "border-black-800 max-w-[50%] my-8" }),
        React.createElement("label", { className: "font-N6 text-white" }, "Description"),
        React.createElement("label", { className: "font-N7 mt-[16px] max-w-[900px] whitespace-pre-wrap ".concat(incident.description ? "text-white" : "text-black-400") }, incident.description || "No description."),
        React.createElement("hr", { className: "border-black-800 max-w-[50%] my-8" }),
        React.createElement("div", { className: "flex flex-row gap-x-4 w-fit" },
            canWrite && (React.createElement(StyledButton, { text: "EDIT", style: StyledButtonStyle.Green, onClick: openEdit })),
            React.createElement(StyledButton, { text: "HISTORY", style: StyledButtonStyle.Gray, onClick: openAudit })),
        React.createElement(Dialog, { open: dialog !== "none", onClose: closeDialog, className: "relative z-50" },
            React.createElement("div", { className: "fixed inset-0 bg-black/30", "aria-hidden": "true" }),
            React.createElement("div", { className: "fixed inset-0 flex items-center justify-center p-4" },
                React.createElement(Dialog.Panel, { className: "mx-auto" },
                    dialog === "edit" && (React.createElement(IncidentEditDialog, { incident: incident, chargers: (_e = site === null || site === void 0 ? void 0 : site.chargers) !== null && _e !== void 0 ? _e : [], powernodes: (_f = site === null || site === void 0 ? void 0 : site.powernodes) !== null && _f !== void 0 ? _f : [], alertSuppressions: alertSuppressions, onClose: closeDialog, onSubmit: submitEdit, onDelete: canWrite ? submitDelete : undefined })),
                    dialog === "audit" && (React.createElement(IncidentAuditLogDialog, { uid: incident.uid, title: incident.title, onClose: closeDialog })))))));
};
